<template>
  <div class="p-6 md:p-10">
    <NumberingSettings ref="numbering" settings-key="companies" default-prefix="ETD-" label="company" />

    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <div>
        <p class="text-xs uppercase tracking-[0.4em] text-amber-500 font-semibold">Manage</p>
        <h1 class="mt-2 text-3xl font-bold text-gray-900">Companies / Customers</h1>
      </div>
      <button
        @click="openAddModal"
        class="rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
      >
        + Add Company / Customer
      </button>
    </div>

    <p v-if="feedback" class="mb-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
      {{ feedback }}
    </p>

    <!-- Search -->
    <div v-if="!loading && companies.length > 0" class="relative mb-6 max-w-sm">
      <svg class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
      </svg>
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search companies / customers..."
        class="w-full rounded-full border border-gray-200 py-2.5 pl-11 pr-4 text-sm focus:outline-none focus:border-amber-500"
      />
    </div>

    <div v-if="loading" class="py-16 text-center text-gray-400">Loading...</div>

    <div v-else-if="companies.length === 0" class="rounded-[24px] border border-dashed border-gray-200 py-16 text-center">
      <p class="text-gray-500">No companies or customers yet.</p>
      <button @click="openAddModal" class="mt-4 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600">
        + Add the first one
      </button>
    </div>

    <div v-else-if="filteredCompanies.length === 0" class="rounded-[24px] border border-dashed border-gray-200 py-16 text-center">
      <p class="text-gray-500">No companies or customers match "{{ searchQuery }}".</p>
    </div>

    <div v-else class="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      <div
        v-for="company in filteredCompanies"
        :key="company.id"
        class="rounded-[24px] border border-gray-100 bg-white p-6 shadow-sm transition hover:shadow-md"
      >
        <p class="text-xs font-semibold uppercase tracking-wide text-amber-600">{{ company.etdId }}</p>
        <h3 class="mt-1 text-lg font-bold text-gray-900">{{ company.name }}</h3>
        <p v-if="company.registrationNumber" class="mt-0.5 text-sm text-gray-500">Reg# {{ company.registrationNumber }}</p>

        <div class="mt-4 rounded-2xl bg-gray-50 px-4 py-3">
          <p class="text-[11px] uppercase tracking-wide text-gray-400">Balance Owed to Al Etimad</p>
          <p class="mt-0.5 text-lg font-bold" :class="balanceFor(company.id) > 0 ? 'text-amber-600' : 'text-gray-900'">
            {{ formatBalance(company.id) }}
          </p>
        </div>

        <div class="mt-4 flex flex-wrap items-center gap-3 text-sm font-semibold">
          <router-link :to="`/admin/ledger/edit/${company.id}`" class="text-amber-600 hover:text-amber-700">
            {{ hasLedger(company.id) ? 'Edit Ledger' : 'Create Ledger' }}
          </router-link>
          <router-link
            v-if="hasLedger(company.id)"
            :to="`/admin/ledger/print/${company.id}`"
            target="_blank"
            class="text-gray-600 hover:text-gray-800"
          >
            Print
          </router-link>
          <button v-if="hasLedger(company.id)" @click="share(company)" class="text-gray-600 hover:text-gray-800">Share</button>
          <button @click="openEditModal(company)" class="text-gray-600 hover:text-gray-800">Edit</button>
          <button @click="confirmDelete(company)" class="text-red-500 hover:text-red-600">Delete</button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 px-4 py-8" @click.self="closeModal">
      <div class="w-full max-w-md rounded-[28px] bg-white p-8 shadow-2xl">
        <h2 class="text-xl font-bold text-gray-900">{{ editingId ? 'Edit' : 'Add' }} Company / Customer</h2>

        <form @submit.prevent="handleSubmit" class="mt-6 space-y-4">
          <div>
            <label class="text-xs uppercase tracking-wide text-gray-500">ETD-ID</label>
            <input :value="formData.etdId" type="text" readonly class="mt-2 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500" />
          </div>
          <div>
            <label class="text-xs uppercase tracking-wide text-gray-500">Customer / Company Name</label>
            <input v-model="formData.name" type="text" required class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label class="text-xs uppercase tracking-wide text-gray-500">Registration Number (optional)</label>
            <input v-model="formData.registrationNumber" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>

          <p v-if="formError" class="text-sm text-red-500">{{ formError }}</p>

          <div class="flex items-center gap-3 pt-2">
            <button type="submit" :disabled="saving" class="rounded-2xl bg-amber-500 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-amber-600 disabled:opacity-60">
              {{ saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Add') }}
            </button>
            <button type="button" @click="closeModal" class="rounded-2xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div v-if="deletingItem" class="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 px-4" @click.self="deletingItem = null">
      <div class="w-full max-w-sm rounded-[28px] bg-white p-8 shadow-2xl text-center">
        <h3 class="text-lg font-bold text-gray-900">Delete this company/customer?</h3>
        <p class="mt-2 text-sm text-gray-500">Their ledger, if any, will stay but won't be linked to a name anymore.</p>
        <div class="mt-6 flex items-center justify-center gap-3">
          <button @click="handleDelete" class="rounded-2xl bg-red-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-red-600">Delete</button>
          <button @click="deletingItem = null" class="rounded-2xl border border-gray-200 px-6 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NumberingSettings from '../../components/NumberingSettings.vue'
import { listItems, addItem, updateItem, deleteItem } from '../../services/dataStore'
import { getSettings, setSettings } from '../../services/settingsStore'
import { formatAmount } from '../../services/currencyStore'
import { shareLink } from '../../utils/shareHelper'

export default {
  name: 'AdminCompanies',
  components: { NumberingSettings },
  data() {
    return {
      loading: true,
      companies: [],
      ledgers: [],
      searchQuery: '',
      feedback: '',
      showModal: false,
      editingId: null,
      formData: { etdId: '', name: '', registrationNumber: '' },
      formError: '',
      saving: false,
      deletingItem: null
    }
  },
  computed: {
    filteredCompanies() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.companies
      return this.companies.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.etdId || '').toLowerCase().includes(q) ||
        (c.registrationNumber || '').toLowerCase().includes(q)
      )
    }
  },
  async mounted() {
    await this.loadAll()
  },
  methods: {
    async loadAll() {
      this.loading = true
      this.companies = await listItems('companies', [])
      this.ledgers = await listItems('ledgers', [])
      this.loading = false
    },
    ledgerFor(companyId) {
      return this.ledgers.find(l => l.companyId === companyId) || null
    },
    hasLedger(companyId) {
      return !!this.ledgerFor(companyId)
    },
    balanceFor(companyId) {
      const ledger = this.ledgerFor(companyId)
      if (!ledger) return 0
      return (ledger.rows || []).reduce(
        (bal, row) => bal + (Number(row.debit) || 0) - (Number(row.credit) || 0),
        0
      )
    },
    formatBalance(companyId) {
      const ledger = this.ledgerFor(companyId)
      return formatAmount(this.balanceFor(companyId), ledger?.currency || 'PKR')
    },
    async share(company) {
      const url = `${window.location.origin}/admin/ledger/print/${company.id}`
      const result = await shareLink(url, `${company.name} - Customer Ledger`)
      if (result.copied) this.showFeedback('Link copied!')
    },
    async openAddModal() {
      const settings = await getSettings('companies', { prefix: 'ETD-', nextNumber: 1001 })
      this.formData = { etdId: `${settings.prefix}${settings.nextNumber}`, name: '', registrationNumber: '' }
      this.editingId = null
      this.formError = ''
      this.showModal = true
    },
    openEditModal(company) {
      this.formData = { etdId: company.etdId, name: company.name, registrationNumber: company.registrationNumber || '' }
      this.editingId = company.id
      this.formError = ''
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    async handleSubmit() {
      this.formError = ''
      this.saving = true
      try {
        if (this.editingId) {
          await updateItem('companies', this.editingId, this.formData)
          this.showFeedback('Changes saved.')
        } else {
          await addItem('companies', this.formData)
          const settings = await getSettings('companies', { prefix: 'ETD-', nextNumber: 1001 })
          await setSettings('companies', { ...settings, nextNumber: settings.nextNumber + 1 })
          if (this.$refs.numbering) await this.$refs.numbering.refresh()
          this.showFeedback('Added successfully.')
        }
        this.showModal = false
        await this.loadAll()
      } catch (e) {
        this.formError = 'Something went wrong while saving. Please try again.'
      } finally {
        this.saving = false
      }
    },
    confirmDelete(company) {
      this.deletingItem = company
    },
    async handleDelete() {
      if (!this.deletingItem) return
      try {
        await deleteItem('companies', this.deletingItem.id)
        this.deletingItem = null
        this.showFeedback('Deleted.')
        await this.loadAll()
      } catch (e) {
        this.deletingItem = null
      }
    },
    showFeedback(message) {
      this.feedback = message
      setTimeout(() => {
        if (this.feedback === message) this.feedback = ''
      }, 3000)
    }
  }
}
</script>
